import { useState, useEffect } from "react";
import { getTenantLegacyId } from "../../../api/tenantContext";
import { basicInfoStore } from "../utils/basicInfoStore";
import { adminAuth } from "../utils/adminAuth";

interface NavItem {
  id: string;
  label: string;
  icon: string;
  badge?: number;
}

interface Props {
  activeSection: string;
  onNavigate: (section: string) => void;
  pendingCount: number;
  onLogout: () => void;
}

const navGroups: { title: string; items: NavItem[] }[] = [
  {
    title: "概览",
    items: [{ id: "dashboard", label: "数据总览", icon: "ri-dashboard-3-line" }],
  },
  {
    title: "会员管理",
    items: [
      { id: "member-audit", label: "会员审核", icon: "ri-user-received-line" },
      { id: "member-list", label: "会员列表", icon: "ri-team-line" },
      { id: "member-import", label: "会员导入", icon: "ri-upload-cloud-2-line" },
      { id: "member-points-import", label: "导入会员积分", icon: "ri-coin-line" },
      { id: "renewal-manager", label: "续费管理", icon: "ri-refresh-line" },
    ],
  },
  {
    title: "内容管理",
    items: [
      { id: "landing-page", label: "落地页管理", icon: "ri-layout-masonry-line" },
      { id: "news-publish", label: "资讯发布", icon: "ri-edit-box-line" },
      { id: "news-list", label: "资讯列表", icon: "ri-newspaper-line" },
      { id: "news-audit", label: "资讯审核", icon: "ri-shield-check-line" },
      { id: "file-manager", label: "文件管理", icon: "ri-folder-3-line" },
    ],
  },
  {
    title: "活动管理",
    items: [
      { id: "activity-list", label: "活动管理", icon: "ri-calendar-event-line" },
      { id: "linked-activities", label: "关联活动", icon: "ri-link" },
    ],
  },
  {
    title: "投票管理",
    items: [
      { id: "vote-manager", label: "投票管理", icon: "ri-survey-line" },
    ],
  },
  {
    title: "运营工具",
    items: [
      { id: "notifications", label: "消息通知", icon: "ri-notification-3-line" },
      { id: "redeem-orders", label: "兑换订单", icon: "ri-shopping-bag-3-line" },
      { id: "feedback", label: "意见反馈", icon: "ri-feedback-line" },
      { id: "data-reports", label: "数据报表", icon: "ri-bar-chart-box-line" },
    ],
  },
  {
    title: "系统设置",
    items: [
      { id: "system-settings", label: "系统设置", icon: "ri-settings-4-line" },
    ],
  },
];

export default function AdminSidebar({ activeSection, onNavigate, pendingCount, onLogout }: Props) {
  const [tenantId, setTenantId] = useState(() => getTenantLegacyId());
  const [brand, setBrand] = useState(() => basicInfoStore.get());

  useEffect(() => {
    return basicInfoStore.subscribe(() => setBrand(basicInfoStore.get()));
  }, []);

  useEffect(() => {
    const sync = () => setTenantId(getTenantLegacyId());
    sync();
    window.addEventListener("fan-tenant-context", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("fan-tenant-context", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const displayName = (brand.name || brand.shortName || "").trim() || "球迷协会";
  const logoSrc = brand.logoUrl?.trim() ?? "";
  const user = adminAuth.getLoggedInUser();
  const userName = (user?.name ?? "").trim() || "后台账号";
  const roleLabel =
    user?.role === "super"
      ? "超级管理员"
      : user?.role === "admin"
        ? "管理员"
        : user?.role === "editor"
          ? "编辑"
          : user?.role === "viewer"
            ? "只读"
            : "";

  const badgeMap: Record<string, number> = {
    "member-audit": pendingCount,
    "news-audit": 1,
  };

  return (
    <aside className="w-60 flex-shrink-0 bg-gray-900 min-h-screen flex flex-col">
      {/* Logo + Tenant Identity */}
      <div className="px-5 pt-5 pb-3 border-b border-gray-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl overflow-hidden flex-shrink-0 bg-gray-800">
            {logoSrc ? (
              <img src={logoSrc} alt="" className="w-full h-full object-contain" />
            ) : (
              <i className="ri-football-line text-red-400 text-lg" aria-hidden />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-bold leading-tight truncate">{displayName}</p>
            <p className="text-gray-400 text-xs">管理后台</p>
          </div>
        </div>

        {/* Tenant Info Card */}
        <div className="bg-gray-800/60 rounded-xl px-3 py-2.5 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-xs">租户 ID</span>
            <span className="text-gray-300 text-xs font-mono truncate max-w-[9rem]" title={tenantId}>
              {tenantId}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-xs">套餐版本</span>
            <span className="text-xs px-1.5 py-0.5 rounded-full font-medium text-amber-400 bg-amber-400/10">
              标准版
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-xs">所属平台</span>
            <span className="text-gray-400 text-xs flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
              FanHub 平台
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navGroups.map((group) => (
          <div key={group.title} className="mb-5">
            <p className="text-gray-500 text-xs font-semibold px-3 mb-1.5 uppercase tracking-wider">
              {group.title}
            </p>
            {group.items.map((item) => {
              const isActive = activeSection === item.id;
              const badge = badgeMap[item.id];
              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm cursor-pointer transition-all mb-0.5 ${
                    isActive
                      ? "bg-red-600 text-white"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <i className={`${item.icon} text-base`}></i>
                    {item.label}
                  </span>
                  {badge && badge > 0 ? (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${isActive ? "bg-red-500 text-white" : "bg-amber-500 text-white"}`}>
                      {badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="px-3 pb-4 border-t border-gray-800 pt-3">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-admin-line text-white text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{userName}</p>
            <p className="text-gray-500 text-xs">{roleLabel || "后台账号"}</p>
          </div>
        </div>

        <button
          onClick={onLogout}
          className="w-full mt-1.5 px-3 py-2 text-gray-500 hover:text-red-400 text-xs flex items-center gap-2 cursor-pointer rounded-lg hover:bg-gray-800 transition-colors"
        >
          <i className="ri-logout-box-r-line"></i>
          退出登录
        </button>
      </div>
    </aside>
  );
}
